import {logger} from '../logger';

/**
 * Open an IndexedDB database and handle the upgrade event.
 * @param name - The name of the database.
 * @param version - The version of the database.
 * @param handleUpgradeNeeded - Callback to handle the upgrade event.
 */
export const openDatabase = (
    name: string,
    version: number,
    handleUpgradeNeeded: (
        db: IDBDatabase,
        event: IDBVersionChangeEvent,
    ) => void,
) =>
    new Promise<{
        db: IDBDatabase;
        close: () => void;
    }>((resolve, reject) => {
        const request = indexedDB.open(name, version);
        const handleUpgrade = (event: IDBVersionChangeEvent) => {
            const db = request.result;
            handleUpgradeNeeded(db, event);
        };

        const unsubscribe = () => {
            request.removeEventListener('upgradeneeded', handleUpgrade);
            request.removeEventListener('error', handleError);
            request.removeEventListener('success', handleSuccess);
        };
        const handleSuccess = () => {
            resolve({
                db: request.result,
                close: () => {
                    request.result.close();
                    unsubscribe();
                },
            });
        };
        const handleError = () => {
            const error = request.error;
            logger.error(
                {error},
                `Error occurred while opening ${name}@v${version} DB`,
            );
            unsubscribe();
            reject(error);
        };
        request.addEventListener('upgradeneeded', handleUpgrade);
        request.addEventListener('error', handleError);
        request.addEventListener('success', handleSuccess);
    });

/**
 * Begin a transaction on the database.
 * @param db - The database to begin the transaction on.
 * @param storeNames - The names of the object stores to include in the transaction.
 * @param mode - The mode of the transaction.
 * @returns The transaction and a function to end the transaction.
 * @throws DOMException - If an error occurs during the transaction.
 */
export const beginTransaction = (
    db: IDBDatabase,
    storeNames: string[],
    mode: IDBTransactionMode,
) => {
    const transaction = db.transaction(storeNames, mode);
    const handleError = () => {
        const error = transaction.error;
        logger.error(
            {error, storeNames, mode, db},
            `Error occurred during ${storeNames} transaction DB.`,
        );
        if (error) {
            throw error;
        }
    };
    const handleComplete = () => {
        logger.info(
            {storeNames, mode, db},
            `Complete ${storeNames} transaction successfully.`,
        );
    };
    const handleAbort = () => {
        logger.info({storeNames, mode, db}, `Abort ${storeNames} transaction!`);
    };
    transaction.addEventListener('error', handleError);
    transaction.addEventListener('abort', handleAbort);
    transaction.addEventListener('complete', handleComplete);
    return {
        transaction,
        endTransaction: () => {
            transaction.removeEventListener('error', handleError);
            transaction.removeEventListener('abort', handleAbort);
            transaction.removeEventListener('complete', handleComplete);
        },
    };
};

/**
 * Wrap IDBRequest in a promise.
 */
export const idbRequest = <T>(storeRequest: IDBRequest<T>, message: string) =>
    new Promise<T>((resolve, reject) => {
        const handleError = () => {
            const error = storeRequest.error;
            logger.error({error}, message);
            reject(error);
        };
        const handleSuccess = () => {
            logger.info({result: storeRequest.result}, message);
            resolve(storeRequest.result);
        };
        storeRequest.addEventListener('error', handleError);
        storeRequest.addEventListener('success', handleSuccess);
    });

export const storageUtils = {
    open: openDatabase,
    beginTransaction,
    request: idbRequest,
};

export type StorageUtils = typeof storageUtils;
